import React, { useState, useEffect } from 'react';
import contextAnalyticsService from '../../../services/contextAnalyticsService';
import {
  ServerIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import logger from '../../../utils/logger';

const ContextCacheMonitor = ({ cacheStats, dateRange, showNotification, compact = false }) => {
  const [loading, setLoading] = useState(false);
  const [topQueries, setTopQueries] = useState([]);
  const [stats, setStats] = useState(cacheStats);

  useEffect(() => {
    if (!cacheStats && !compact) {
      fetchCacheData();
    } else {
      setStats(cacheStats);
    }
  }, [cacheStats, dateRange]);

  const fetchCacheData = async () => {
    try {
      setLoading(true);

      const filters = buildFilters();

      const [statsResponse, topQueriesResponse] = await Promise.all([
        contextAnalyticsService.getCacheStatistics(filters),
        contextAnalyticsService.getTopCachedQueries(10)
      ]);

      if (statsResponse.success) {
        setStats(statsResponse.data);
      }

      if (topQueriesResponse.success) {
        setTopQueries(topQueriesResponse.data);
      }
    } catch (error) {
      logger.error('Error fetching cache data:', error);
      if (showNotification) {
        showNotification('Failed to load cache data', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const buildFilters = () => {
    const filters = {};
    const now = Date.now();

    switch (dateRange) {
      case 'today': {
        const today = new Date(now);
        today.setHours(0, 0, 0, 0);
        filters.startDate = today.toISOString();
        break;
      }
      case 'week':
        filters.startDate = new Date(now - 7 * 24 * 60 * 60 * 1000).toISOString();
        break;
      case 'month':
        filters.startDate = new Date(now - 30 * 24 * 60 * 60 * 1000).toISOString();
        break;
      default:
        break;
    }

    return filters;
  };

  const handleInvalidateCache = async (identifier) => {
    try {
      const response = await contextAnalyticsService.invalidateCache(identifier);

      if (response.success) {
        showNotification?.('Cache invalidated successfully', 'success');
        fetchCacheData();
      }
    } catch (error) {
      logger.error('Error invalidating cache:', error);
      showNotification?.('Failed to invalidate cache', 'error');
    }
  };

  if (compact) {
    return (
      <div className="bg-white dark:bg-[#1a1a1a] rounded-lg shadow p-6">
        <div className="flex items-center mb-4">
          <ServerIcon className="h-6 w-6 text-green-600 mr-2" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Cache Performance
          </h3>
        </div>

        {stats ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 dark:bg-[#222222] rounded-lg p-3">
                <p className="text-sm text-gray-500 dark:text-gray-400">Hit Rate</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {stats.hitRate}%
                </p>
              </div>
              <div className="bg-gray-50 dark:bg-[#222222] rounded-lg p-3">
                <p className="text-sm text-gray-500 dark:text-gray-400">Cache Size</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {stats.currentCacheSize}
                </p>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-200 dark:border-gray-800">
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className="text-gray-500 dark:text-gray-400">Total Queries</dt>
                  <dd className="text-gray-900 dark:text-white font-medium">{stats.totalQueries}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500 dark:text-gray-400">Cache Hits</dt>
                  <dd className="text-green-600 dark:text-green-400 font-medium">{stats.cacheHits}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500 dark:text-gray-400">Cache Misses</dt>
                  <dd className="text-red-600 dark:text-red-400 font-medium">{stats.cacheMisses}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-gray-500 dark:text-gray-400">Performance Gain</dt>
                  <dd className="text-blue-600 dark:text-blue-400 font-medium">{stats.performanceImprovement}%</dd>
                </div>
              </dl>
            </div>
          </div>
        ) : (
          <div className="text-center text-gray-500 dark:text-gray-400 py-4">
            No cache data available
          </div>
        )}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Cache Statistics */}
      <div className="bg-white dark:bg-[#1a1a1a] rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center">
            <ServerIcon className="h-6 w-6 text-green-600 mr-2" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Cache Performance
            </h3>
          </div>
        </div>

        <div className="p-6">
          {stats ? (
            <div className="space-y-6">
              {/* Key Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-lg p-6">
                  <div className="flex items-center justify-between">
                    <CheckCircleIcon className="h-10 w-10 text-green-600" />
                    <div className="text-right">
                      <p className="text-3xl font-bold text-green-900 dark:text-green-100">
                        {stats.hitRate}%
                      </p>
                      <p className="text-sm text-green-700 dark:text-green-300">Hit Rate</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-lg p-6">
                  <div className="flex items-center justify-between">
                    <ClockIcon className="h-10 w-10 text-blue-600" />
                    <div className="text-right">
                      <p className="text-3xl font-bold text-blue-900 dark:text-blue-100">
                        {stats.performanceImprovement}%
                      </p>
                      <p className="text-sm text-blue-700 dark:text-blue-300">Performance Gain</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 rounded-lg p-6">
                  <div className="flex items-center justify-between">
                    <ServerIcon className="h-10 w-10 text-purple-600" />
                    <div className="text-right">
                      <p className="text-3xl font-bold text-purple-900 dark:text-purple-100">
                        {stats.currentCacheSize}
                      </p>
                      <p className="text-sm text-purple-700 dark:text-purple-300">Cache Size</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Detailed Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 dark:bg-[#222222] rounded-lg p-4">
                  <p className="text-sm text-gray-500 dark:text-gray-400">Total Queries</p>
                  <p className="text-xl font-semibold text-gray-900 dark:text-white mt-1">
                    {stats.totalQueries}
                  </p>
                </div>

                <div className="bg-gray-50 dark:bg-[#222222] rounded-lg p-4">
                  <p className="text-sm text-gray-500 dark:text-gray-400">Cache Hits</p>
                  <p className="text-xl font-semibold text-green-600 dark:text-green-400 mt-1">
                    {stats.cacheHits}
                  </p>
                </div>

                <div className="bg-gray-50 dark:bg-[#222222] rounded-lg p-4">
                  <p className="text-sm text-gray-500 dark:text-gray-400">Cache Misses</p>
                  <p className="text-xl font-semibold text-red-600 dark:text-red-400 mt-1">
                    {stats.cacheMisses}
                  </p>
                </div>

                <div className="bg-gray-50 dark:bg-[#222222] rounded-lg p-4">
                  <p className="text-sm text-gray-500 dark:text-gray-400">Avg Response (Hit)</p>
                  <p className="text-xl font-semibold text-gray-900 dark:text-white mt-1">
                    {Math.round(stats.avgHitDuration)}ms
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-center text-gray-500 dark:text-gray-400 py-8">
              No cache data available
            </p>
          )}
        </div>
      </div>

      {/* Top Cached Queries */}
      {topQueries && topQueries.length > 0 && (
        <div className="bg-white dark:bg-[#1a1a1a] rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Most Accessed Cached Items
            </h3>
          </div>

          <div className="p-6">
            <div className="space-y-3">
              {topQueries.map((query, idx) => (
                <div
                  key={query._id}
                  className="flex items-center justify-between p-4 bg-gray-50 dark:bg-[#222222] rounded-lg hover:bg-gray-100 dark:hover:bg-[#2a2a2a]"
                >
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <span className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-200 font-semibold text-sm">
                        {idx + 1}
                      </span>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {query.identifier}
                        </p>
                        <div className="flex items-center space-x-4 mt-1 text-sm text-gray-500 dark:text-gray-400">
                          <span>Type: {query.contextType}</span>
                          <span>•</span>
                          <span>Accesses: {query.accessCount}</span>
                          <span>•</span>
                          <span>Last: {new Date(query.lastAccessedAt).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleInvalidateCache(query.identifier)}
                    className="ml-4 p-2 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                    title="Invalidate cache"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContextCacheMonitor;
